<template>
  <div
    class="base-radio"
    :class="{ 'base-radio--compact': isCompact }"
  >
    <label>
      <div class="base-radio__wrapper">
        <input
          v-if="!props.toggleMode"
          :checked="props.modelValue === props.value"
          :tabindex="props.tabindex"
          :disabled="props.readonly"
          type="radio"
          @change="handleChangeradio"
          @keydown.enter.prevent="handleEnterKeyRadio"
        >
        <input
          v-else
          :checked="!!props.modelValue"
          :tabindex="props.tabindex"
          :disabled="props.readonly"
          type="checkbox"
          @change="handleToggleChange"
          @keydown.enter.prevent="handleEnterKeyToggle"
        >
        <div class="base-radio__icon">
          <BaseIcon
            name="vuesax:tick-circle-bold"
            :color="props.checkedColor"
            fill-type="solid"
            size="var(--radio-icon-size)"
          />
        </div>
        <div class="base-radio__radio" />
      </div>

      <slot>
        <p v-if="props.label">
          {{ props.label }}
        </p>
      </slot>
    </label>
  </div>
</template>

<script setup lang="ts">
import BaseIcon from '../icon/index.vue'

const props = withDefaults(
  defineProps<{
    modelValue?: string | boolean
    tabindex?: number
    value?: string
    label?: string
    readonly?: boolean
    /**
     * Переключатель boolean с видом радио (чтобы можно было снять отметку).
     */
    toggleMode?: boolean
    /** Цвет обводки, галочки и фокуса в отмеченном состоянии. */
    checkedColor?: string
  }>(),
  {
    checkedColor: 'var(--color-foreground-primary)',
    toggleMode: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | boolean): void
}>()

const checkedcolorcss = computed(() => props.checkedColor)

const isCompact = computed(() => props.toggleMode && !props.label)

function handleChangeradio(event: Event) {
  if (props.readonly) {
    return
  }

  event.preventDefault()
  emit('update:modelValue', props.value ?? '')
}

function handleEnterKeyRadio() {
  if (props.readonly) {
    return
  }

  emit('update:modelValue', props.value ?? '')
}

function handleToggleChange(event: Event) {
  if (props.readonly) {
    return
  }

  event.preventDefault()
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.checked)
}

function handleEnterKeyToggle() {
  if (props.readonly) {
    return
  }

  emit('update:modelValue', !props.modelValue)
}
</script>

<style lang="scss" scoped>
.base-radio {
  @include typo-text-secondary;

  --radio-size: 22px;
  --radio-icon-size: calc(var(--radio-size) + 4px);
  --radio-checked-color: v-bind(checkedcolorcss);

  font-weight: 400;

  @include bp-media('xs', 'sm') {
    --radio-size: 16px;
  }

  &--compact label {
    width: auto;
  }

  label {
    width: 100%;
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 8px;
  }

  &__wrapper {
    flex-shrink: 0;
    position: relative;
    align-self: flex-start;
    width: var(--radio-icon-size);
    height: var(--radio-icon-size);
  }

  &__radio {
    position: absolute;
    left: 50%;
    top: 50%;
    width: var(--radio-size);
    height: var(--radio-size);
    transform: translate(-50%, -50%);
    border: 2px solid var(--color-foreground-primary);
    border-radius: 50%;
    z-index: 1;
    box-sizing: border-box;
  }

  .base-radio__icon {
    opacity: 0;
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;
    z-index: 2;
    pointer-events: none;
  }

  input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
  }

  input:checked + &__icon {
    opacity: 1;
  }

  input:checked ~ &__radio {
    border-color: var(--radio-checked-color);
  }

  input:focus ~ &__radio {
    box-shadow: 0 0 13.8px -3px var(--color-foreground-primary);
  }

  input:checked:focus ~ &__radio {
    box-shadow: 0 0 13.8px -3px var(--radio-checked-color);
  }

  p {
    flex: 1;
    width: 100%;
    overflow: hidden;
  }
}
</style>
